import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { KollelFormData } from '../schemas/kollelFormSchema';
import { StudyTimeOptions } from '@/constants/studyTimeOptions';

const ChoiceCard = ({ selected, label, onClick }: { selected: boolean; label: string; onClick: () => void }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    className={`text-start rounded-lg px-4 py-3 transition-colors ${
      selected
        ? 'border-[1.5px] border-(--border-secondary-strong) bg-(--surface-base)'
        : 'border border-(--border-lowest) bg-(--surface-base) hover:border-(--border-strong)'
    }`}
  >
    <span className={selected ? 'body-md-semibold' : 'body-md-regular'}>{label}</span>
  </button>
);

/**
 * Step 3 of 4 — "סדרי לימוד": the Figma's "האם סדרי הלימוד של האברכים בכולל זהים או שונים זה
 * מזה?" question. "זהים" picks one default for the whole kollel; "שונים" additionally shows a
 * per-avrech row (the Figma's "קביעת סדר יום אברך" list) so each of step 2's avrechs can get its
 * own slot, falling back to the default when left unset.
 */
const KollelScheduleStep = () => {
  const { t } = useTranslation();
  const { watch, setValue } = useFormContext<KollelFormData>();

  const scheduleMode = watch('scheduleMode');
  const defaultStudyTime = watch('defaultStudyTime');
  const avrechs = watch('avrechs') ?? [];

  const setOverride = (supportedId: number, value: StudyTimeOptions | '') => {
    setValue(
      'avrechs',
      avrechs.map((a) => (a.supportedId === supportedId ? { ...a, studyTimeOverride: value || undefined } : a)),
    );
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <h3 className="heading-h3-semibold text-(--font-primary)">{t('kollels.wizard.step3.title')}</h3>

      <fieldset className="flex flex-col gap-3">
        <legend className="body-md-semibold text-(--font-secondary) mb-1">{t('kollels.wizard.step3.modeLabel')}</legend>
        <div className="grid grid-cols-2 max-md:grid-cols-1 gap-3" role="radiogroup">
          <ChoiceCard
            selected={scheduleMode === 'same'}
            label={t('kollels.wizard.step3.modeSame')}
            onClick={() => setValue('scheduleMode', 'same')}
          />
          <ChoiceCard
            selected={scheduleMode === 'different'}
            label={t('kollels.wizard.step3.modeDifferent')}
            onClick={() => setValue('scheduleMode', 'different')}
          />
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-3">
        <legend className="body-md-semibold text-(--font-secondary) mb-1">
          {scheduleMode === 'same' ? t('kollels.form.studyTimeLabel') : t('kollels.wizard.step3.defaultLabel')}
        </legend>
        <div className="grid grid-cols-3 max-md:grid-cols-2 gap-3" role="radiogroup">
          {Object.values(StudyTimeOptions).map((option) => (
            <ChoiceCard
              key={option}
              selected={defaultStudyTime === option}
              label={t(`StudyTimeOptions.${option}`)}
              onClick={() => setValue('defaultStudyTime', option)}
            />
          ))}
        </div>
      </fieldset>

      {scheduleMode === 'different' && (
        <fieldset className="flex flex-col gap-2">
          <legend className="body-md-semibold text-(--font-secondary) mb-1">
            {t('kollels.wizard.step3.perAvrechLabel')}
          </legend>
          <div className="flex flex-col divide-y divide-(--border-lowest) border border-(--border-main) rounded-lg">
            {avrechs.length === 0 && (
              <p className="body-sm-regular text-(--font-secondary) p-3">{t('kollels.wizard.step3.noAvrechs')}</p>
            )}
            {avrechs.map((avrech) => (
              <div key={avrech.supportedId} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="body-sm-regular truncate">{`${avrech.firstName} ${avrech.lastName}`.trim()}</span>
                <select
                  className="border border-(--border-main) rounded-lg px-2 py-1 body-sm-regular shrink-0"
                  value={avrech.studyTimeOverride ?? ''}
                  onChange={(e) => setOverride(avrech.supportedId, e.target.value as StudyTimeOptions | '')}
                >
                  <option value="">{t('kollels.wizard.step3.useDefault')}</option>
                  {Object.values(StudyTimeOptions).map((option) => (
                    <option key={option} value={option}>
                      {t(`StudyTimeOptions.${option}`)}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
};

export default KollelScheduleStep;
